import { useEffect } from 'react';
import { useSegment } from '@hooks/useSegment';
import { useAuth } from '@hooks/useAuth';
import {
  RootDiv,
  ModalDiv,
  ErrorImg,
  ErrorTitleTypography,
  ErrorSubtitleTypography,
  ErrorButton,
  ErrorButtonTypography,
  LoaderContainer,
} from './ErrorModal.styles';
import { usePermissions } from '@hooks/usePermissions';
import { getRoute } from '@utils';
import { useRouter } from 'next/router';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  GetInsuranceQuoteResponse,
  GetInsuranceQuoteParams,
  getInsuranceQuote,
} from '@queries/userInsurance/queries/getInsuranceQuote';
import { useOrderState } from '@hooks/useOrderState';
import {
  SetCheckoutOrderDataResponse,
  SetCheckoutOrderDataParams,
  setCheckoutOrderData,
} from '@queries/order/mutations/setCheckoutOrderData';
import { CircularProgress } from '@mui/material';
import { PHONE_NUMBER } from '@config/environment';

export enum ErrorModalTypes {
  REGISTRY = 'registryError',
  FINTOC = 'fintocError',
  NOT_ALLOWED_ITEMS = 'notAllowedItemsError',
  INVALID_INSURANCE = 'invalidInsuranceError',
  DEFAULT = 'default',
  NONE = 'none',
}

interface ErrorModalProps {
  handleHideError: () => void;
  uuid: string;
  errorType: ErrorModalTypes;
  refetchOrder?: () => void;
}

export function ErrorModal(props: ErrorModalProps) {
  const { handleHideError, uuid, errorType, refetchOrder } = props;

  const registryError = errorType === ErrorModalTypes.REGISTRY;
  const fintocError = errorType === ErrorModalTypes.FINTOC;
  const notAllowedItemsError = errorType === ErrorModalTypes.NOT_ALLOWED_ITEMS;
  const invalidInsuranceError = errorType === ErrorModalTypes.INVALID_INSURANCE;

  const { appUser: user } = useAuth();
  const { userCan } = usePermissions();
  const { track } = useSegment();
  const router = useRouter();
  const { order: currentOrder } = useOrderState();

  const isLocalVendor = userCan('LOCAL_VENDOR');

  const id = uuid?.slice(0, 6).toUpperCase() ?? '';

  const [getInsuranceQuoteLazy, { loading }] = useLazyQuery<
    GetInsuranceQuoteResponse,
    GetInsuranceQuoteParams
  >(getInsuranceQuote(), {
    fetchPolicy: 'network-only',
  });

  const [setCheckoutData] = useMutation<
    SetCheckoutOrderDataResponse,
    SetCheckoutOrderDataParams
  >(setCheckoutOrderData());

  const currentOrderPaymentInformation = {
    useMekiCredits: currentOrder.metadata.useMekiCredits,
    chargeDeliveryCost: currentOrder.metadata.chargeDeliveryCost,
    localVendor: {
      paymentMethod: currentOrder.metadata.localVendor?.paymentMethod ?? null,
      mixPayments: currentOrder.metadata.localVendor?.mixPayments ?? null,
    },
    assistantVendor: {
      paymentMethod:
        currentOrder.metadata.assistantVendor?.paymentMethod ?? null,
    },
    sendEmail: currentOrder.metadata.sendEmail,
    userInfo: currentOrder.metadata.userInfo,
    installments: currentOrder.metadata.installments,
  };

  const handleRefreshQuote = async () => {
    try {
      const response = await getInsuranceQuoteLazy({
        variables: {
          params: {
            rut: currentOrder.metadata.insuranceInfo.rut,
          },
        },
      });

      const newQuoteInfo = response.data?.getInsuranceQuote;
      const cleanReimbursedProducts = newQuoteInfo?.reimbursedProducts?.map(
        (product) => {
          if (product.coverage) {
            return {
              sku: +product.sku,
              observation: product.observation,
              unitReimbursement: product.unitReimbursement,
              unitDeductible: product.unitDeductible,
              unitCopay: product.unitCopay,
              coverage: product.coverage,
              quantity: product.quantity,
            };
          }
        },
      );
      await setCheckoutData({
        variables: {
          params: {
            orderId: currentOrder.id,
            replace: true,
            metadata: {
              ...currentOrderPaymentInformation,
              insuranceInfo: {
                claimId: +newQuoteInfo?.claimId,
                rut: newQuoteInfo?.metadata?.rut ?? '',
                insuranceId: newQuoteInfo?.insuranceId ?? null,
                totalReimbursement: newQuoteInfo?.totalReimbursement ?? 0,
                totalDeductible: newQuoteInfo?.totalDeductible ?? 0,
                reimbursedProducts: cleanReimbursedProducts ?? [],
              },
            },
          },
        },
      });
      refetchOrder ?? refetchOrder();
    } catch (error) {
      void router.push(
        isLocalVendor
          ? getRoute('checkoutPaymentMethodLocal')
          : getRoute('checkoutPaymentMethod'),
      );
    }
  };

  const onActionClick = () => {
    if (registryError) {
      void track('add payment error contact', {
        userId: user?.id,
        error: 'registry error',
        paymentMethodType: fintocError ? 'FINTOC' : 'ONE PAY',
      });
    } else {
      void track('payment error contact', {
        userId: user?.id,
        error: 'other error',
        paymentMethodType: fintocError ? 'FINTOC' : 'ONE PAY',
      });
    }
    window.open(
      `https://api.whatsapp.com/send?phone=${
        PHONE_NUMBER.CLEAN
      }&text=${encodeURI(
        `Hola! Tengo problemas en pagar mi compra. El número de mi orden es ${id}.`,
      )}`,
      '_blank',
    );
  };

  const onButtonClick = async () => {
    if (notAllowedItemsError) {
      void router.push(getRoute('home', { s: 'open' }));
      return;
    }
    if (invalidInsuranceError) {
      await handleRefreshQuote();
    }
    handleHideError();
  };

  const defaultSubtitle = (
    <>
      Intenta nuevamente, prueba con otro método de pago o{' '}
      <span onClick={onActionClick}>contáctanos para ayudarte</span>
    </>
  );

  const errorTexts = {
    [ErrorModalTypes.REGISTRY]: {
      title: 'Hubo un problema registrando tu tarjeta',
      subtitle: defaultSubtitle,
    },
    [ErrorModalTypes.FINTOC]: {
      title: 'Hubo un problema y no se realizó el pago',
      subtitle: defaultSubtitle,
    },
    [ErrorModalTypes.NOT_ALLOWED_ITEMS]: {
      title: isLocalVendor
        ? 'Algunos productos ya no estan disponibles'
        : 'Se actualizó tu carro',
      subtitle: isLocalVendor
        ? 'Se actualizó la orden. Revisa el carrito y vuelve a intentar'
        : 'Revisa tus productos e intenta nuevamente',
    },
    [ErrorModalTypes.INVALID_INSURANCE]: {
      title: 'Se actualizó el aporte de tu seguro',
      subtitle: 'Revisa la nueva cobertura e intenta nuevamente',
    },
    [ErrorModalTypes.DEFAULT]: {
      title: 'Hubo un problema y no se realizó el pago',
      subtitle: defaultSubtitle,
    },
  };

  useEffect(
    function registryError() {
      if (registryError) {
        void track('add payment error', {
          userId: user?.id,
          error: 'registry error',
          paymentMethodType: fintocError ? 'FINTOC' : 'ONE PAY',
        });
      } else {
        void track('payment error', {
          userId: user?.id,
          error: 'registry error',
          paymentMethodType: fintocError ? 'FINTOC' : 'ONE PAY',
        });
      }
    },
    [fintocError, registryError, track, user?.id],
  );

  if (loading) {
    return (
      <RootDiv>
        <ModalDiv>
          <LoaderContainer>
            <CircularProgress size={30} color="info" />
          </LoaderContainer>
          <ErrorTitleTypography>Actualizando seguro...</ErrorTitleTypography>
          <ErrorSubtitleTypography>
            Por favor, espera un momento
          </ErrorSubtitleTypography>
        </ModalDiv>
      </RootDiv>
    );
  }

  return (
    <RootDiv>
      <ModalDiv>
        <ErrorImg src={'/assets/images/warningIcon.png'} />

        <ErrorTitleTypography>
          {errorTexts[errorType].title}
        </ErrorTitleTypography>
        <ErrorSubtitleTypography>
          {errorTexts[errorType].subtitle}
        </ErrorSubtitleTypography>

        <ErrorButton onClick={onButtonClick}>
          <ErrorButtonTypography>
            {notAllowedItemsError ? 'Ver productos' : 'Volver a intentar'}
          </ErrorButtonTypography>
        </ErrorButton>
      </ModalDiv>
    </RootDiv>
  );
}
